import React, { useEffect, useState } from "react";
import axios from "axios";
import AdminAPI from "../utils/adminApi";
import { useCountry } from "../context/CountryContext";

// ✅ NEW: general withdrawals and referral withdrawals live under
// different base paths (/api/withdrawal and /api/referral-withdrawal),
// while AdminAPI's baseURL is locked to /api/admin. Two small instances
// here reuse the exact same admin-token interceptor pattern as
// adminApi.js, pointed at the correct base URLs.
const AdminWithdrawalAPI = axios.create({
  baseURL: "https://alkebulanfkalertbackend.onrender.com/api/withdrawal",
  headers: { "Content-Type": "application/json" },
});
AdminWithdrawalAPI.interceptors.request.use((req) => {
  const token = localStorage.getItem("adminToken");
  if (token) req.headers.Authorization = `Bearer ${token}`;
  return req;
});

const AdminReferralWithdrawalAPI = axios.create({
  baseURL: "https://alkebulanfkalertbackend.onrender.com/api/referral-withdrawal",
  headers: { "Content-Type": "application/json" },
});
AdminReferralWithdrawalAPI.interceptors.request.use((req) => {
  const token = localStorage.getItem("adminToken");
  if (token) req.headers.Authorization = `Bearer ${token}`;
  return req;
});

const Withdrawals = () => {
  const { isGhana, config } = useCountry();

  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | NG | GH
  const [debugInfo, setDebugInfo] = useState(null);

  const theme = isGhana
    ? {
        currencySymbol: "₵",
        flag: "🇬🇭",
        primaryBtn: "bg-yellow-500 hover:bg-yellow-600 text-black",
      }
    : {
        currencySymbol: "₦",
        flag: "🇳🇬",
        primaryBtn: "bg-green-600 hover:bg-green-700 text-white",
      };

  const fetchWithdrawals = async () => {
    try {
      setLoading(true);
      setDebugInfo(null);

      const debug = { general: null, referral: null };

      const generalRes = await AdminWithdrawalAPI.get("/all").catch((err) => {
        debug.general = `${err.response?.status || "?"} ${err.response?.data?.message || err.message}`;
        return { data: [] };
      });

      const referralRes = await AdminReferralWithdrawalAPI.get("/pending").catch((err) => {
        debug.referral = `${err.response?.status || "?"} ${err.response?.data?.message || err.message}`;
        return { data: [] };
      });

      setDebugInfo(debug);

      const general = (Array.isArray(generalRes.data) ? generalRes.data : []).map((w) => ({
        ...w,
        _source: "general",
      }));

      const referral = (Array.isArray(referralRes.data) ? referralRes.data : []).map((w) => ({
        ...w,
        _source: "referral",
      }));

      setWithdrawals([...general, ...referral]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWithdrawals();
  }, []);

  const markAsPaid = async (w) => {
    if (!window.confirm("Mark this withdrawal as paid?")) return;
    try {
      if (w._source === "referral") {
        await AdminReferralWithdrawalAPI.patch(`/${w._id}/process`);
      } else {
        await AdminWithdrawalAPI.put(`/pay/${w._id}`);
      }
      alert("✅ Marked as paid");
      fetchWithdrawals();
    } catch (err) {
      console.error(err);
      alert("❌ Failed to mark as paid");
    }
  };

  const filteredWithdrawals = withdrawals.filter((w) => {
    if (filter === "all") return true;
    return w.country === filter;
  });

  const countNG = withdrawals.filter((w) => w.country === "NG" || !w.country).length;
  const countGH = withdrawals.filter((w) => w.country === "GH").length;

  const getSymbol = (w) => {
    if (w.currencySymbol) return w.currencySymbol;
    if (w.country === "GH") return "₵";
    if (w.country === "NG") return "₦";
    return theme.currencySymbol;
  };

  const getFlag = (w) => {
    if (w.country === "GH") return "🇬🇭";
    if (w.country === "NG") return "🇳🇬";
    return theme.flag;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "paid":
        return "bg-green-100 text-green-700";
      case "pending":
        return "bg-yellow-100 text-yellow-700";
      case "rejected":
      case "failed":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold">Withdrawals</h2>
          <p className="text-xs text-gray-500 mt-1">
            Total: {withdrawals.length} · 🇳🇬 Nigeria: {countNG} · 🇬🇭 Ghana: {countGH}
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setFilter("all")}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition ${
              filter === "all" ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-700"
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilter("NG")}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition ${
              filter === "NG" ? "bg-green-600 text-white" : "bg-gray-200 text-gray-700"
            }`}
          >
            🇳🇬 Nigeria
          </button>
          <button
            onClick={() => setFilter("GH")}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition ${
              filter === "GH" ? "bg-yellow-500 text-black" : "bg-gray-200 text-gray-700"
            }`}
          >
            🇬🇭 Ghana
          </button>
        </div>
      </div>

      {debugInfo && (debugInfo.general || debugInfo.referral) && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-300 text-xs text-red-800 space-y-1">
          <p className="font-bold">🔬 Debug — fetch errors detected:</p>
          {debugInfo.general && <p>General withdrawals (/withdrawal/all): {debugInfo.general}</p>}
          {debugInfo.referral && <p>Referral withdrawals (/referral-withdrawal/pending): {debugInfo.referral}</p>}
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-x-auto">
        {loading ? (
          <p className="p-6 text-center text-gray-500">Loading withdrawals...</p>
        ) : filteredWithdrawals.length === 0 ? (
          <p className="p-6 text-center text-gray-500">No withdrawals found.</p>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-200">
              <tr>
                <th className="p-3 text-left">Country</th>
                <th className="p-3 text-left">Type</th>
                <th className="p-3 text-left">User</th>
                <th className="p-3 text-left">Amount</th>
                <th className="p-3 text-left">Bank</th>
                <th className="p-3 text-left">Account</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-left">Action</th>
              </tr>
            </thead>

            <tbody>
              {filteredWithdrawals.map((w) => {
                const symbol = getSymbol(w);
                const flag = getFlag(w);

                return (
                  <tr key={w._id} className="border-t hover:bg-gray-50 transition">
                    <td className="p-3">
                      <span className="text-xl">{flag}</span>
                      <div className="text-xs text-gray-500">
                        {w.country || "NG"}
                      </div>
                    </td>

                    <td className="p-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          w._source === "referral"
                            ? "bg-purple-100 text-purple-700"
                            : "bg-blue-100 text-blue-700"
                        }`}
                      >
                        {w._source === "referral" ? "Referral" : "General"}
                      </span>
                      {w._source === "referral" && w.referralCountAtRequest != null && (
                        <div className="text-xs text-gray-400 mt-1">
                          {w.referralCountAtRequest} referrals
                        </div>
                      )}
                    </td>

                    <td className="p-3">
                      <div className="font-medium">{w.user?.name || w.userName || "Unknown"}</div>
                      <div className="text-sm text-gray-500">{w.user?.email || w.userEmail}</div>
                      {w.user?.phone && (
                        <div className="text-xs text-gray-400">{w.user.phone}</div>
                      )}
                    </td>

                    <td className="p-3 font-semibold">
                      {symbol}{Number(w.amount || 0).toLocaleString()}
                    </td>
                    <td className="p-3">{w.bankName || "—"}</td>
                    <td className="p-3 font-mono text-sm">{w.accountNumber || "—"}</td>

                    <td className="p-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold capitalize ${getStatusBadge(w.status)}`}>
                        {w.status}
                      </span>
                    </td>

                    <td className="p-3">
                      {w.status !== "paid" && (
                        <button
                          onClick={() => markAsPaid(w)}
                          className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-sm font-semibold transition"
                        >
                          Mark as Paid
                        </button>
                      )}
                      {w.status === "paid" && (
                        <span className="text-green-600 text-sm font-semibold">✓ Paid</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Withdrawals;
